import { useQueryClient } from "@tanstack/react-query";
import MarkdownEditor from "features/markdown/blocks/MarkdownEditor";
import AvatarEditor, {
  randomAvatarOptions,
  avatarOptionsToUrl,
} from "features/people/blocks/AvatarEditor/AvatarEditor";
import type { Person } from "features/people/data/types";
import { useDataQueryRefetch } from "hooks/useData";
import { useFlag } from "hooks/useFlags";
import { useUser } from "hooks/useUser";
import React, { useCallback, useMemo, useState } from "react";

import { apiHost } from "../../../../consts";

import PersonalProfile from "./PersonalProfile";

const PersonalProfileEdit: React.FC = () => {
  const { person } = useUser();

  const avatarEditorEnabled = useFlag("avatarEditorEnabled");

  const personId = person?.id;

  const { data: personData, refetch: refetchPerson } =
    useDataQueryRefetch<Person>(`people/${personId}/`, false);

  const queryClient = useQueryClient();

  const [firstName, setFirstName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [about, setAbout] = useState("");
  const [contactInformation, setContactInformation] = useState("");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const [isEditing, setIsEditing] = useState(false);

  // Инициализируем форму после загрузки данных
  useMemo(() => {
    if (personData) {
      setFirstName(person.firstName || "");
      setAvatarUrl(
        personData.avatarUrl || avatarOptionsToUrl(randomAvatarOptions()),
      );
      setAbout(personData.about || "");
      setContactInformation(personData.contactInformation || "");
    }
  }, [personData?.id]); // срабатывает при смене загруженного пользователя

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSaved(false);

    try {
      const res = await fetch(`${apiHost}/solutions/api/people/${personId}/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          firstName,
          ...(avatarEditorEnabled ? { avatarUrl } : {}),
          about,
          contactInformation,
        }),
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Save failed: ${res.status} ${txt}`);
      }

      setSaved(true);
      setIsEditing(false);
    } catch (err: any) {
      setError(err.message || "Unknown error");
    } finally {
      setSaving(false);
      setIsEditing(false);
      refetchPerson();
      // useUser() кэширует свою копию person (имя, аватар) отдельно от people/{id}/
      queryClient.invalidateQueries({ queryKey: [`user/status/`] });
    }
  };

  const onEditClick = useCallback(() => {
    setIsEditing(true);
  }, []);

  const contactsSharingEnabled = useFlag("contactsSharingEnabled");

  if (!personData) return <div>Loading…</div>;

  if (!isEditing)
    return (
      <PersonalProfile
        id={personData.id}
        onEditClick={onEditClick}
      />
    );

  return (
    <div className="personal-profile">
      <h2>Редактирование профиля</h2>

      <form onSubmit={onSubmit}>
        <div style={{ marginBottom: 8 }}>
          <label>
            Имя
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              style={{ display: "block", width: "100%" }}
            />
          </label>
        </div>

        {avatarEditorEnabled && (
          <div style={{ marginBottom: 16 }}>
            <AvatarEditor
              avatarUrl={avatarUrl}
              onChangeAvatar={setAvatarUrl}
            />
          </div>
        )}

        <div style={{ marginBottom: 8 }}>
          <div>О себе</div>
          <MarkdownEditor
            initialValue={about}
            onChange={setAbout}
            showModeSwitch
            height="300px"
          />
        </div>

        {contactsSharingEnabled && (
          <div style={{ marginBottom: 8 }}>
            <label>
              Контактная информация
              <textarea
                value={contactInformation}
                onChange={(e) => setContactInformation(e.target.value)}
                rows={5}
                style={{ display: "block", width: "100%" }}
              />
            </label>
          </div>
        )}

        <button
          type="submit"
          disabled={saving}
        >
          {saving ? "Сохраняется…" : "Сохранить"}
        </button>
      </form>

      {error && <div style={{ color: "red", marginTop: 8 }}>{error}</div>}
      {saved && <div style={{ color: "green", marginTop: 8 }}>Сохранено.</div>}
    </div>
  );
};

export default PersonalProfileEdit;
